import { useEffect, useState } from 'react';
import { getActiveLocations } from '@/api/admin/location';
import {
	add,
	getProviderOptions,
	ImportTypeProvider,
	ImportTypeProviderSchema,
} from '@/api/admin/provider';
import { Button } from '@/components/ui/button';
import { ComboboxSelect } from '@/components/ui/custom/combobox-select';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CirclePlus } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function AddImportTypeProvider({
	importTypeId,
}: {
	importTypeId: number;
}) {
	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (newImportTypeProvider: ImportTypeProvider) =>
			add(newImportTypeProvider),
		onSuccess: () => {
			toast.success(`Success! Provider has been successfully added.`, {
				dismissible: true,
			});
			setOpen(false);

			//Invalidate queries to refresh data
			queryClient.invalidateQueries({
				queryKey: [`provider-${importTypeId}`],
			});
			queryClient.invalidateQueries({
				queryKey: [`provider-options`],
			});

			reset();
		},
		onError: () => {
			toast.error(`Error! Provider could not be added.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<ImportTypeProvider>({
		resolver: zodResolver(ImportTypeProviderSchema),
		mode: 'all',
		defaultValues: {
			importTypeId: importTypeId,
			compLocId: undefined,
			providerId: undefined,
			program: undefined,
			prefix: undefined,
		},
	});

	const { reset, watch } = form;

	const { data: locationOptions, isLoading: isLoadingLocationOptions } =
		useQuery({
			queryKey: [`location-options`],
			queryFn: () => getActiveLocations(),
			staleTime: 5 * 60 * 1000, // Cache for 5 minutes
		});

	const selectedCompLocId = watch('compLocId');
	const { data: providerOptions, isLoading: isLoadingProviderOptions } =
		useQuery({
			queryKey: [`provider-options`, selectedCompLocId],
			queryFn: () => getProviderOptions(importTypeId, selectedCompLocId!),
			staleTime: 5 * 60 * 1000,
			enabled: !!selectedCompLocId,
		});

	useEffect(() => {
		form.resetField('providerId');
	}, [selectedCompLocId, form]);

	const onSubmit: SubmitHandler<ImportTypeProvider> = (
		values: ImportTypeProvider,
	) => mutate(values);

	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" onClick={() => setOpen(true)}>
							<CirclePlus />
							Add New Provider
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Add a new provider</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent>
					<SheetHeader>
						<SheetTitle>Add a New Provider</SheetTitle>
						<SheetDescription></SheetDescription>
					</SheetHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="space-y-4">
								<FormField
									control={form.control}
									name="importTypeId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Import Type ID</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} disabled={true} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="compLocId"
									render={({ field }) => (
										<ComboboxSelect
											field={field}
											options={locationOptions}
											valueKey="compLocId"
											displayKey="brandName"
											isLoading={isLoadingLocationOptions}
											label="Company Location"
											placeholder="Select company location..."
											searchPlaceholder="Search company location..."
											required={true}
											emptyMessage="No company location found."
											loadingMessage="Loading company locations..."
										/>
									)}
								/>
								<FormField
									control={form.control}
									name="providerId"
									render={({ field }) => (
										<ComboboxSelect
											field={field}
											options={providerOptions}
											valueKey="value"
											displayKey="label"
											isLoading={isLoadingProviderOptions}
											label="Provider"
											placeholder={
												selectedCompLocId
													? 'Select provider...'
													: 'Select a company location first'
											}
											searchPlaceholder="Search providers..."
											required={true}
											emptyMessage="No provider found."
											loadingMessage="Loading providers..."
											disabled={!selectedCompLocId}
										/>
									)}
								/>
								<FormField
									control={form.control}
									name="program"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												Program<span className="text-red-500">*</span>
											</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="prefix"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												Prefix<span className="text-red-500">*</span>
											</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="mt-9 flex justify-end">
								<Button
									type="submit"
									disabled={!form.formState.isValid || form.formState.isSubmitting}
								>
									{form.formState.isSubmitting ? 'Adding...' : 'Add'}
								</Button>
							</div>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
